/* eslint-disable no-magic-numbers */
import * as PIXI from 'pixi.js-legacy';
window.PIXI = PIXI;
import 'pixi-filters';
import TWEEN from 'tween.js';
import tinycolor from './libs/tinycolor';
import imgMissing from './img/missing.jpg';
import imgAvatar from './img/avatar.png';
import imgGradient from './img/gradient.png';
import imgBg from './img/bg.jpg';
import imgLogo from './img/logo.png';
import imgBanner from './img/banner.png';
import DummyData from './DummyData';
import Vibrant from 'node-vibrant';

import { resolutionFullHD, fontFamily } from './app/contest/constants';
import { getRel, getRelY, getRelX } from './app/contest/utils/getRel';
import bustImageCache from './app/contest/utils/bustImageCache';
import { getRandomColor, assignUniqueColor } from './app/contest/utils/getColour';
import ContestBg from './app/contest/ContestBg';
import ContestLogo from './app/contest/ContestLogo';
import ContestImage from './app/contest/ContestImage';
import ContestText from './app/contest/ContestText';
import { ItemBg, ItemAvatar, ItemBar, ItemBarText } from './app/contest/ItemTween';
import UserAvatar from './app/contest/UserAvatar';
import { TeamBanner } from './app/contest/Team';

// detect requestAnimationFrame support and fallback to setTimeout
// some smart TVs and Epiphany have the requestAnimationFrame method but it doesn't actually work
import AnimationFrame from 'animation-frame';
AnimationFrame.shim();

const Contest = {
	enabled: true,
	started: false,
	duration: 1000,  // base animation duration
	repeat: 1,  // how many times to repeat the intro animation (1 for only once)
	repeatedTimes: 0,  // how many times we repeated the animation so far
	renderer: {},

	init: function() {
		this.enabled = true;

		// setup the stage
		this.stage = new PIXI.Container();
		this.renderer = PIXI.autoDetectRenderer({
			width: Screen.elements.contest.width(),
			height: Screen.elements.contest.height(),
			transparent: true,
		});

		this.loader = new PIXI.Loader();
		Screen.elements.contest.append(this.renderer.view);

		window.rendererWidth = Screen.elements.contest.width();
		window.rendererHeight = Screen.elements.contest.height();

		$(window).on('screenResized.contest', this.resize.bind(this));
	},

	destroy: function() {

		// clear any timeouts, unbind events and reset variables
		this.enabled = false;
		this.started = false;
		TWEEN.removeAll();
		$(window).off('screenResized.contest');
		this.renderer.destroy(true);
		this.stage.destroy({ children: true });
		this.loader.reset();
		Screen.elements.contest.empty();
		this.done?.();
	},

	start: function(rawData, done) {
		const that = this;
		if (this.started) {
			log('Contest already started!');
			done?.();
			return;
		} else {
			this.started = true;
		}

		this.done = done;

		// show loader
		this.loaderElement = $('<div class="contest-loader-bg">' +
			'<img src="' + imgLogo + '" alt="Plecto" class="contest-loader-logo" />' +
			'<div class="contest-loader">' +
				'<div class="contest-loader-bar" style="width:0"></div>' +
			'</div>' +
		'</div>');
		Screen.elements.contest.append(this.loaderElement);
		this.loaderElementBar = this.loaderElement.find('.contest-loader-bar');

		// prepare the data
		this.prepareData(rawData, function() {

			// once data is ready, load the resources
			that.loader
				.add({ name: 'missing', url: imgMissing, crossOrigin: true })
				.add({ name: 'bg', url: imgBg, crossOrigin: true })
				.add({ name: 'logo', url: imgLogo, crossOrigin: true })
				.add({ name: 'avatar', url: imgAvatar, crossOrigin: true })
				.add({ name: 'banner', url: imgBanner, crossOrigin: true })
				.add({ name: 'gradient', url: imgGradient, crossOrigin: true });

			if (that.data.mode !== 'member') {
				if (that.data.organization.logo) {
					that.loader.add({ url: that.data.organization.logo, crossOrigin: true });
				}
				if (that.data.contest.prize_image) {
					that.loader.add({ url: that.data.contest.prize_image, crossOrigin: true });
				}
			}

			// load each avatar only once
			const avatars = {};
			for (let i = 0; i < that.data.items.length; i++) {
				avatars[ that.data.items[ i ].avatar ] = 1;
				if (that.data.items[ i ].members) {
					for (let m = 0; m < that.data.items[ i ].members.length; m++) {
						avatars[ that.data.items[ i ].members[ m ].avatar ] = 1;
					}
				}
			}
			$.each(avatars, function(avatar) {
				try {
					that.loader.add({ url: avatar, crossOrigin: true });
				} catch (e) {

					// catch Resource with name "..." already exists.
				}
			});
			that.loader.load(that.setup.bind(that));

			that.loader.onProgress.add(that.loadProgressHandler.bind(that));
			that.loader.onError.add(function(error, loader, resource) {
				// eslint-disable-next-line no-undef
				log('Failed to load "' + resource.name + '"! Using a default instead.');

				// replace missing image
				if (error.message === 'Failed to load element using IMG') {
					delete loader.resources[ resource.name ];
					loader.add({ name: resource.name, url: imgMissing, crossOrigin: true });
				}
			});
		});
	},

	prepareData: function(data, callback) {

		// use the provided data otherwise some dummy if isDemo
		// eslint-disable-next-line no-nested-ternary
		this.data = data ? data : (Screen.options.isDemo ? DummyData.contestWithUsers : data);

		// go through all avatars and set the default if not there
		for (let i = 0; i < this.data.items.length; i++) {
			if (!this.data.items[ i ].avatar) {
				this.data.items[ i ].avatar = 'avatar';  // use default avatar
			} else {

				// temporary cache busting
				this.data.items[ i ].avatar = bustImageCache(this.data.items[ i ].avatar);
			}
			if (this.data.items[ i ].members) {
				for (let m = 0; m < this.data.items[ i ].members.length; m++) {
					if (!this.data.items[ i ].members[ m ].avatar) {
						this.data.items[ i ].members[ m ].avatar = 'avatar';  // use default avatar
					} else {

						// temporary cache busting
						// eslint-disable-next-line max-len
						this.data.items[ i ].members[ m ].avatar = bustImageCache(this.data.items[ i ].members[ m ].avatar);
					}
				}
			}
		}

		// temporary cache busting
		if (data.mode !== 'member') {
			if (this.data.organization.logo) {
				this.data.organization.logo = bustImageCache(this.data.organization.logo);
			}
			if (this.data.contest.prize_image) {
				this.data.contest.prize_image = bustImageCache(this.data.contest.prize_image);
			}
		}

		// this.analyzeDataColors(callback);
		callback();
	},

	analyzeDataColors: function(callback) {

		// analyze all images with Vibrant
		const that = this;
		let colorsToAnalyse = 0;
		let analysedColors = 0;
		let vibrant;

		// logo
		if (this.data.organization.logo) {
			colorsToAnalyse += 1;
			vibrant = new Vibrant(this.data.organization.logo);
			vibrant.getPalette(function(err, palette) {

				// this runs async
				assignUniqueColor(that.data.organization, palette);
				analysedColors += 1;
			});
		} else {
			this.data.organization.color = {};
		}

		// contest image
		if (this.data.contest.prize_image) {
			colorsToAnalyse += 1;
			vibrant = new Vibrant(this.data.contest.prize_image);
			vibrant.getPalette(function(err, palette) {

				// this runs async
				assignUniqueColor(that.data.contest, palette);
				analysedColors += 1;
			});
		} else {
			this.data.contest.color = {};
		}

		// avatars
		$.each(this.data.items, function(i, item) {
			colorsToAnalyse += 1;
			vibrant = new Vibrant(item.avatar);
			vibrant.getPalette(function(err, palette) {

				// this runs async
				assignUniqueColor(item, palette);
				analysedColors += 1;
			});
		});

		// wait for Vibrant to finish async
		function checkIfFinished() {
			if (colorsToAnalyse === analysedColors) {
				// eslint-disable-next-line no-undef
				log('finished Vibrant');
				callback();
			} else {
				// eslint-disable-next-line no-undef
				log('waiting for Vibrant');
				setTimeout(checkIfFinished, 100);
			}
		}
		checkIfFinished();
	},

	loadProgressHandler: function(loader, resource) {

		// update progress bar
		this.loaderElementBar.css('width', parseInt(loader.progress) + '%');
		// eslint-disable-next-line no-undef
		log(parseInt(loader.progress), resource.name);
	},

	setup: function(loader, resources) {
		if (!this.enabled) {
			return;
		}
		this.resources = resources;
		this.loaderElement.remove();
		if (this.data.mode === 'member') {
			this.createStageShort();
		} else {
			this.createStage();
		}
		requestAnimationFrame(this.animate.bind(this));
	},

	createStage: function() {
		const that = this;

		// the contest summary
		this.startContestIntro(function(oldObjects) {

			// the team/user overview
			that.startContestantsOverview(oldObjects, function(oldObjects) {
				// the user/teams intro
				that.startContestantsHighlight(oldObjects, function(oldObjects) {
					// finished
					that.finished();
					that.destroyOldObjects(oldObjects);
				});
			});
		});
	},

	createStageShort: function() {
		const that = this;

		// the user/teams intro
		this.startContestantsHighlight([], function(oldObjects) {

			// finished
			that.destroyOldObjects(oldObjects);
			that.finished();
		});
	},

	finished: function() {
		this.enabled = false;
		setTimeout(function() {
			this.done?.();
			// allows some time for the animation loop to cleanly finish
			Screen.changeMode('slides');
		}, 200);
	},

	animate: function(time) {
		if (!this.enabled) {
			return;
		}
		requestAnimationFrame(this.animate.bind(this));
		TWEEN.update(time);
		this.renderer.render(this.stage);
	},

	resize: function() {
		this.renderer.resize(Screen.elements.contest.width(), Screen.elements.contest.height());
		window.rendererWidth = Screen.elements.contest.width();
		window.rendererHeight = Screen.elements.contest.height();
	},

	destroyOldObjects: function(oldObjects) {

		// remove old objects from the stage
		for (let i = 0; i < oldObjects.length; i++) {
			this.stage.removeChild(oldObjects[ i ]);
		}
	},

	startContestIntro: function(callback) {
		const that = this;
		const bg = ContestBg(this.resources.bg.texture, this.renderer.width, this.renderer.height);
		const logoRelFontSize = 80 * (this.renderer.width / resolutionFullHD);
		// eslint-disable-next-line max-len
		const logo = ContestLogo(this.data.organization.logo, this.data.organization.name, logoRelFontSize, this.resources);
		const textRelFontSize = 60 * (this.renderer.width / resolutionFullHD);
		const textTitle = ContestText(textRelFontSize, this.data.contest.name);
		const textPrize = ContestText(textRelFontSize, this.data.contest.prize_name, Screen.trans('Contest prize'));
		// eslint-disable-next-line max-len
		const textTarget = ContestText(textRelFontSize, this.data.target.formatted_value, Screen.trans('Contest target'));
		const objectsToDestroy = [bg, logo, textTitle, textPrize, textTarget];
		textTitle.position.y = getRelY(0.28);
		textPrize.position.y = getRelY(0.5);
		textTarget.position.y = getRelY(0.72);
		this.stage.addChild(bg);
		this.stage.addChild(textTitle);
		if (this.data.contest.prize_name) {
			this.stage.addChild(textPrize);
		}
		if (this.data.target.value) {
			this.stage.addChild(textTarget);
		}
		this.stage.addChild(logo);

		// fade the bg
		const bgFadeIn = new TWEEN.Tween(bg)
			.easing(TWEEN.Easing.Exponential.Out)
			.to({ alpha: 1 }, this.duration);

		// show the logo
		logo.alpha = 0;
		const logoFadeIn = new TWEEN.Tween(logo)
			.easing(TWEEN.Easing.Exponential.Out)
			.to({ alpha: 1 }, this.duration * 3);
		let logoScale = logo.scale.x * 0.7;
		const logoScaleIn = new TWEEN.Tween(logo.scale)
			.easing(TWEEN.Easing.Exponential.Out)
			.to({ x: logoScale, y: logoScale }, this.duration * 3);
		const logoPosTo = new TWEEN.Tween(logo.position)
			.easing(TWEEN.Easing.Exponential.Out)
			.to({ x: getRelX(0.25), y: getRelY(0.1) }, this.duration);
		logoScale = logo.scale.x * 0.3;
		const logoScaleTo = new TWEEN.Tween(logo.scale)
			.easing(TWEEN.Easing.Exponential.Out)
			.to({ x: logoScale, y: logoScale }, this.duration);
		bgFadeIn.chain(logoFadeIn, logoScaleIn);
		logoFadeIn.chain(logoPosTo, logoScaleTo);

		// show the contest image
		if (this.data.contest.prize_image) {
			const texture = this.resources[ this.data.contest.prize_image ].texture;
			const contestImage = ContestImage(texture);
			objectsToDestroy.push(contestImage);
			this.stage.addChild(contestImage);
			contestImage.alpha = 0;
			const contestImageIn = new TWEEN.Tween(contestImage)
				.easing(TWEEN.Easing.Exponential.Out)
				.to({ alpha: 1 }, this.duration);
			logoPosTo.chain(contestImageIn);
		}

		// show the contest text
		const textTitleIn = new TWEEN.Tween(textTitle.position)
			.easing(TWEEN.Easing.Exponential.Out)
			.to({ x: getRelX(0.05) }, this.duration);
		const textPrizeIn = new TWEEN.Tween(textPrize.position)
			.easing(TWEEN.Easing.Exponential.Out)
			.to({ x: getRelX(0.05) }, this.duration);
		const textTargetIn = new TWEEN.Tween(textTarget.position)
			.easing(TWEEN.Easing.Exponential.Out)
			.to({ x: getRelX(0.05) }, this.duration);
		textPrizeIn.delay(this.duration * 0.25);
		textTargetIn.delay(this.duration * 0.5);
		logoScaleTo.chain(textTitleIn, textPrizeIn, textTargetIn);

		// start all animations
		const startTween = new TWEEN.Tween().to({}, this.duration / 4);
		startTween.chain(bgFadeIn);
		startTween.start();
		textTargetIn.onComplete(function() {
			setTimeout(function() {
				// start users/teams intro
				callback(objectsToDestroy);
			}, that.duration * 5);
		});
	},

	startContestantsOverview: function(oldObjects, callback) {
		const that = this;
		const bg = ContestBg(this.resources.bg.texture, this.renderer.width, this.renderer.height);
		this.stage.addChild(bg);
		bg.alpha = 0;
		const relFontSize = 40 * (this.renderer.width / resolutionFullHD);
		const title = new PIXI.Text(Screen.trans('Contestants'), {
			fontWeight: 'normal',
			fontSize: relFontSize + 'px',
			fontFamily: fontFamily,
			fill: '#FFFFFF',
			align: 'center',
			wordWrap: true,
			wordWrapWidth: getRelX(0.46),
		});
		title.anchor = new PIXI.Point(0.5, 0.5);
		title.position = getRel(0.5, -0.1);
		this.stage.addChild(title);

		// fade in the bg
		const bgFadeIn = new TWEEN.Tween(bg)
			.easing(TWEEN.Easing.Exponential.Out)
			.to({ alpha: 1 }, this.duration * 2);

		// show the title
		const titleIn = new TWEEN.Tween(title.position)
			.easing(TWEEN.Easing.Exponential.Out)
			.to({ x: getRelX(0.5), y: getRelY(0.05) }, this.duration);

		const itemsIn = new TWEEN.Tween({}).to({}, this.duration * 0.2);
		bgFadeIn.chain(titleIn, itemsIn);
		bgFadeIn.start();

		// show items one by one
		const finishedCallback = function() {
			that.destroyOldObjects(oldObjects);
			// finish the user animation
			callback([bg, title]);
		};
		if (that.data.contest.contest_type === 'teams') {
			this.animateInTeams(this.getAllAnimationItems(), itemsIn, finishedCallback);
		} else {
			this.animateInUsers(this.getAllAnimationItems(), itemsIn, finishedCallback);
		}
	},

	animateInTeams: function(items, startTween, callback) {
		const that = this;
		let obj, pos, tweenFade, tweenPos, lastTween, col;
		const tweensPerPage = {};
		const maxPerPage = 5;
		let pages = 0;
		let page = 0;
		let delay = 0;
		const itemDelay = this.duration * 0.3;
		const pagesDelay = this.duration * 6;

		// create objects and tweens
		for (let i = 0; i < items.length; i++) {
			page = Math.floor(i / maxPerPage);
			if (!tweensPerPage.hasOwnProperty(page)) {

				// start new page
				pages += 1;
				delay = 0;
				tweensPerPage[ page ] = {
					objects: [],
					tweens: [],
				};
			}
			col = i - ((pages - 1) * maxPerPage);
			obj = TeamBanner(items[ i ], this.resources);
			pos = getRel(0.13 + (0.185 * col), 0.18);
			obj.position = new PIXI.Point(pos.x, pos.y + getRelY(0.1));
			obj.alpha = 0;
			tweenFade = new TWEEN.Tween(obj)
				.easing(TWEEN.Easing.Exponential.In)
				.to({ alpha: 1 }, this.duration * 0.3);
			tweenPos = new TWEEN.Tween(obj.position)
				.easing(TWEEN.Easing.Exponential.Out)
				.to({ x: pos.x, y: pos.y }, this.duration * 2);
			tweenFade.delay(delay);
			tweenPos.delay(delay);
			tweensPerPage[ page ].tweens.push(tweenFade);
			tweensPerPage[ page ].tweens.push(tweenPos);
			tweensPerPage[ page ].objects.push(obj);
			delay += itemDelay;
			this.stage.addChild(obj);
		}

		// chain tweens
		for (page = 0; page < pages; page++) {
			lastTween = tweensPerPage[ page ].tweens[ tweensPerPage[ page ].tweens.length - 1 ];
			lastTween.onComplete(function(currentPage) {
				const nextStartTween = new TWEEN.Tween({}).to({}, pagesDelay);
				let hasNextPage = false;
				// eslint-disable-next-line max-len
				if (tweensPerPage[ currentPage + 1 ] && tweensPerPage[ currentPage + 1 ].tweens && tweensPerPage[ currentPage + 1 ].tweens.length) {
					hasNextPage = true;
				}
				this.chain(nextStartTween);  // "this" is the lastTween
				nextStartTween.onComplete(function() {

					// remove previous page
					that.destroyOldObjects(tweensPerPage[ currentPage ].objects);
					if (!hasNextPage) {

						// all pages finished
						callback();
					}
				});

				// chain the next page's tweens
				if (hasNextPage) {
					nextStartTween.chain.apply(this, tweensPerPage[ currentPage + 1 ].tweens);
				}
			}.bind(lastTween, page));
		}
		startTween.chain.apply(startTween, tweensPerPage[ 0 ].tweens);
	},

	animateInUsers: function(items, startTween, callback) {
		const that = this;
		let item, obj, tweenFade, tweenScale, lastTween;
		const tweensPerPage = {};
		let delay = 0;
		const len = items.length;
		const maxRows = 4;
		const maxCols = 8;
		const largeVersionMax = 8;
		const itemDelay = this.duration * 0.05;
		const pagesDelay = this.duration * 3;
		let rows = 1;
		let cols = len;
		let pages = 1;
		if (cols > maxCols) {
			cols = maxCols;
			rows = Math.ceil(len / cols);
		}
		if (rows > maxRows) {
			rows = maxRows;
			pages = Math.ceil(len / (cols * rows));
		}
		if (len <= largeVersionMax) {

			// use large avatars
			cols = 4;
			rows = 2;
		}
		let i = 0;

		// create objects and tweens
		for (let page = 0; page < pages; page++) {
			tweensPerPage[ page ] = {
				tweens: [],
				objects: [],
			};
			delay = 0;
			for (let row = 0; row < rows; row++) {
				for (let col = 0; col < cols; col++) {
					item = items[ i ];
					if (item) {
						if (len > largeVersionMax) {

							// small avatars
							obj = UserAvatar(this.resources, item, getRelX(0.07));
							obj.position = getRel(0.08 + (col * 0.12), 0.18 + (row * 0.22));
						} else {

							// large avatars
							obj = UserAvatar(this.resources, item, getRelX(0.14), 40);
							obj.position = getRel(0.125 + (col * 0.25), 0.28 + (row * 0.44));
						}
						obj.alpha = 0;
						obj.scale = new PIXI.Point(0.5, 0.5);
						this.stage.addChild(obj);
						tweenFade = new TWEEN.Tween(obj)
							.easing(TWEEN.Easing.Exponential.In)
							.to({ alpha: 1 }, this.duration * 0.1);
						tweenScale = new TWEEN.Tween(obj.scale)
							.easing(TWEEN.Easing.Elastic.Out)
							.to({ x: 1, y: 1 }, this.duration);
						tweenFade.delay(delay);
						tweenScale.delay(delay);
						tweensPerPage[ page ].tweens.push(tweenFade);
						tweensPerPage[ page ].tweens.push(tweenScale);
						tweensPerPage[ page ].objects.push(obj);
						delay += itemDelay;
					}
					i += 1;
				}
			}
		}

		// chain tweens
		for (let page = 0; page < pages; page++) {
			lastTween = tweensPerPage[ page ].tweens[ tweensPerPage[ page ].tweens.length - 1 ];
			lastTween.onComplete(function(currentPage) {
				const nextStartTween = new TWEEN.Tween({}).to({}, pagesDelay);
				let hasNextPage = false;
				// eslint-disable-next-line max-len
				if (tweensPerPage[ currentPage + 1 ] && tweensPerPage[ currentPage + 1 ].tweens && tweensPerPage[ currentPage + 1 ].tweens.length) {
					hasNextPage = true;
				}
				this.chain(nextStartTween);  // "this" is the lastTween
				nextStartTween.onComplete(function() {

					// remove previous page
					that.destroyOldObjects(tweensPerPage[ currentPage ].objects);
					if (!hasNextPage) {

						// all pages finished
						callback();
					}
				});

				// chain the next page's tweens
				if (hasNextPage) {
					nextStartTween.chain.apply(this, tweensPerPage[ currentPage + 1 ].tweens);
				}
			}.bind(lastTween, page));
		}
		startTween.chain.apply(startTween, tweensPerPage[ 0 ].tweens);
	},

	startContestantsHighlight: function(oldObjects, callback) {
		this.startNextItemTween(oldObjects, callback);
	},
	getAllAnimationItems: function() {

		// copy the array because we are gonna change it
		return this.data.items.slice();
	},

	startNextItemTween: function(oldObjects, callback, items) {
		if (!items) {
			items = this.getAllAnimationItems();
			items.reverse();
		}
		const that = this;
		const item = items.pop();
		const color = item.color && item.color.unique ? tinycolor(item.color.unique) : getRandomColor();
		const texture = this.resources[ item.avatar ].texture;
		const alt = items.length % 2;
		const newBg = ItemBg(texture, color, this.renderer.width, this.renderer.height);
		const avatarCont = ItemAvatar(texture, this.resources.gradient.texture, color, alt);
		const bar = ItemBar(color, alt);
		const targetText = item.target ? item.target.formatted_value : that.data.target.formatted_value;
		const targetTextLabel = Screen.trans(this.data.mode === 'member' ? '' : 'target');
		// the perfect font size for a 1920x1080 resolution
		const targetTextFontSize = 60 * (this.renderer.width / resolutionFullHD);
		const text = ItemBarText(targetTextFontSize, color, item.name, targetText, targetTextLabel, alt);

		// add the newBg on top and hide it
		this.stage.addChild(newBg);
		this.stage.addChild(bar);
		this.stage.addChild(avatarCont);
		this.stage.addChild(text);

		// fade the newBg in and scale it a bit
		newBg.alpha = 0;
		const finalScale = new PIXI.Point(newBg.scale.x * 1.1, newBg.scale.y * 1.1);
		newBg.scale = new PIXI.Point(newBg.scale.x * 1.8, newBg.scale.y * 1.8);
		const bgFadeIn = new TWEEN.Tween(newBg)
			.easing(TWEEN.Easing.Exponential.Out)
			.to({ alpha: 1 }, this.duration);
		const bgScaleOutDuration = this.data.mode === 'member' ? this.duration * that.data.duration : this.duration * 5;
		const bgScaleOut = new TWEEN.Tween(newBg.scale)
			.easing(TWEEN.Easing.Exponential.Out)
			.to({ x: finalScale.x, y: finalScale.y }, bgScaleOutDuration);

		bgFadeIn.onComplete(function() {
			that.destroyOldObjects(oldObjects);
		});

		// slide the avatar in
		avatarCont.scale = new PIXI.Point(2.0, 2.0);
		const avatarSize = getRelX(0.5);
		let pos = {};
		if (alt) {
			pos = { x: getRelX(1) - (avatarSize / 2.2), y: getRelY(1) - (avatarSize / 2.2) };
		} else {
			pos = { x: getRelX(0) + (avatarSize / 2.2), y: getRelY(1) - (avatarSize / 2.2) };
		}
		const avatarIn = new TWEEN.Tween(avatarCont.position)
			.easing(TWEEN.Easing.Exponential.Out)
			.to(pos, that.duration);
		const avatarScaleIn = new TWEEN.Tween(avatarCont.scale)
			.easing(TWEEN.Easing.Exponential.Out)
			.to({ x: 1, y: 1 }, that.duration);
		const avatarScaleOut = new TWEEN.Tween(avatarCont.scale)
			.easing(TWEEN.Easing.Quadratic.In)
			.to({ x: 1.05, y: 1.05 }, that.duration * 5);
		const avatarRotateOut = new TWEEN.Tween(avatarCont)
			.easing(TWEEN.Easing.Quadratic.In)
			.to({ rotation: Math.PI * 0.02 * (alt ? -1 : 1) }, that.duration * 5);
		avatarScaleIn.chain(avatarScaleOut);

		// slide the bar in
		const barIn = new TWEEN.Tween(bar.position)
			.easing(TWEEN.Easing.Exponential.Out)
			.to({ x: getRelX(0.5), y: getRelY(0.7) }, that.duration);
		const barRotateOut = new TWEEN.Tween(bar)
			.to({ rotation: Math.PI * 0.02 * (alt ? -1 : 1) }, that.duration * 5);
		barIn.delay(that.duration / 3);
		barRotateOut.delay(that.duration / 3);

		// show the text
		const textIn = new TWEEN.Tween(text.position)
			.easing(TWEEN.Easing.Exponential.Out)
			.to({ x: getRelX(0.5), y: getRelY(0.5) }, that.duration / 2);
		const textRotateOut = new TWEEN.Tween(text)
			.to({ rotation: Math.PI * 0.02 * (alt ? 1 : -1) }, that.duration * 5);
		const textScaleOut = new TWEEN.Tween(text.scale)
			.to({ x: 1.1, y: 1.1 }, that.duration * 5);
		textIn.delay(that.duration / 2);
		textRotateOut.delay(that.duration / 3);
		textScaleOut.delay(that.duration / 3);

		// start all animations, but delayed
		const startTween = new TWEEN.Tween().to({}, this.duration / 4);
		startTween.chain(
			bgFadeIn, bgScaleOut,
			avatarIn, avatarRotateOut, avatarScaleIn,
			barIn, barRotateOut,
			textIn, textRotateOut, textScaleOut
		);
		startTween.start();

		bgScaleOut.onComplete(function() {
			that.repeatedTimes += 1;

			// continue on next item
			if (items.length) {
				that.startNextItemTween([newBg, avatarCont, bar, text], callback, items);
			} else if (that.repeatedTimes < that.repeat) {

				// run the animation again
				that.startNextItemTween([newBg, avatarCont, bar, text], callback);
			} else {
				callback([newBg, avatarCont, bar, text]);
			}
		});
	},

};

export default Contest;
